// @services/stubs/StubListingInferenceReviewPortalClient.ts
import type { OptionTs, TaskEitherTs } from "@ps-bridge/interop";
import type { ListingInferenceReviewPortalClient } from "@services/clients/ListingInferenceReviewPortalClient";
import {
    decodeListingInferenceEvidenceQueryResult,
    decodeListingInferenceManualDecisionResult,
    decodeListingInferenceRefusal,
    decodeListingInferenceWorkspaceReadResult,
    type ListingInferenceApiDecodeResult,
    type ListingInferenceEvidenceQuery,
    type ListingInferenceEvidenceQueryResult,
    type ListingInferenceJsonPayload,
    type ListingInferenceManualDecisionRequest,
    type ListingInferenceManualDecisionResult,
    type ListingInferenceReviewError,
    type ListingInferenceWorkspaceReadResult,
} from "@services/clients/ListingInferenceReviewPortalApi";
import type {
    ListingInferenceClaimKind,
    ListingInferenceDisposition,
    ListingInferenceListingId,
} from "@ps-bridge/models/brands/listingInferenceReviewPortal";
import { taskEitherLeft, taskEitherOf } from "@utils/effects/branded";
import * as Option from "@utils/Option";

export type StubListingInferenceReviewPortalFailureMode =
    | "none"
    | "readDenied"
    | "writeDenied"
    | "unsafeFactPromotionRefused"
    | "networkError"
    | "responseDecodeError";

const STUB_TRACE = {
    traceId: "stub-listing-inference-trace",
    requestId: "stub-listing-inference-request",
};

const STUB_LISTING_ID = "018f3ad5-39d4-7bd8-9f79-5bb5f6f34c92";
const STUB_CLAIM_ID = "claim-party-1";
const STUB_SOURCE_REF = "source-call-1";
const STUB_DECIDED_AT = "2026-05-12T00:00:00Z";
const jsonNull = (): ListingInferenceJsonPayload => JSON.parse("null");

const buildRefusal = (
    error: "readDenied" | "writeDenied" | "unsafeFactPromotionRefused",
    message: string,
): ListingInferenceReviewError => {
    const decoded = decodeListingInferenceRefusal({
        error,
        message,
        trace: STUB_TRACE,
    });

    if (decoded.type === "ok") {
        return decoded.value;
    }

    return {
                error: "responseDecodeError",
                message: decoded.message,
                trace: Option.none(),
    };
};

const failureFromMode = (mode: StubListingInferenceReviewPortalFailureMode): ListingInferenceReviewError => {
    switch (mode) {
        case "readDenied":
            return buildRefusal("readDenied", "listing inference read permission denied; actorId and readAllowed are required");
        case "writeDenied":
            return buildRefusal(
                "writeDenied",
                "listing inference decision write permission denied; actorId and writeAllowed are required",
            );
        case "unsafeFactPromotionRefused":
            return buildRefusal(
                "unsafeFactPromotionRefused",
                "unsafeFactPromotion requests are refused; unresolved, rejected, and conflicted claims remain gaps or prompts",
            );
        case "networkError":
            return {
                error: "networkError",
                message: "The listing inference review portal service is unavailable.",
                trace: Option.none(),
            };
        case "responseDecodeError":
        case "none":
            return {
                error: "responseDecodeError",
                message: "Unable to decode listing inference review portal response.",
                trace: Option.none(),
            };
    }
};

const decodeOrError = <T>(
    result: ListingInferenceApiDecodeResult<T>,
): TaskEitherTs<ListingInferenceReviewError, T> => {
    if (result.type === "ok") {
        return taskEitherOf(result.value) as TaskEitherTs<ListingInferenceReviewError, T>;
    }

    return taskEitherLeft({
        error: "responseDecodeError",
        message: result.message,
        trace: Option.none(),
    });
};

const maybeFailure = <T>(
    mode: StubListingInferenceReviewPortalFailureMode,
    success: () => TaskEitherTs<ListingInferenceReviewError, T>,
): TaskEitherTs<ListingInferenceReviewError, T> => {
    if (mode === "none") {
        return success();
    }

    return taskEitherLeft(failureFromMode(mode));
};

const stubProvenance = () => ({
    sourceRef: STUB_SOURCE_REF,
    span: {
        startMs: 1000,
        endMs: 2400,
    },
    speaker: "broker",
    observedAt: STUB_DECIDED_AT,
});

const stubClaim = (listingId: string = STUB_LISTING_ID) => ({
    id: STUB_CLAIM_ID,
    listingId,
    kind: "partyIdentity",
    value: "Acme Holdings LLC",
    disposition: "proposed",
    provenance: stubProvenance(),
});

const stubGap = (listingId: string = STUB_LISTING_ID) => ({
    question: "Confirm whether the contracting party name is current.",
    listingId,
    evidenceSource: STUB_SOURCE_REF,
    provenance: stubProvenance(),
});

const stubSummary = () => ({
    proposed: 1,
    confirmed: 0,
    rejected: 0,
    conflicted: 0,
});

const stubWorkspacePayload = (listingId: string = STUB_LISTING_ID) => {
    const claims = [stubClaim(listingId)];
    const gaps = [stubGap(listingId)];
    const manualDecisions: readonly ListingInferenceJsonPayload[] = [];
    const outstandingReviews = [
        {
            item: stubGap(listingId),
            owner: "broker-reviewer",
            reason: "Confirm authority before accepting claim.",
            dueAt: STUB_DECIDED_AT,
        },
    ];
    const effectiveDispositionSummary = stubSummary();
    const workspace = {
        listingId,
        claims,
        outstandingReviews,
        gaps,
        manualDecisions,
        effectiveDispositionSummary,
    };

    return {
        listingId,
        workspace,
        claims,
        outstandingReviews,
        gaps,
        manualDecisions,
        effectiveDispositionSummary,
        nonFactStatus: "containsUnresolvedGaps",
        trace: STUB_TRACE,
    };
};

const stubEvidencePayload = (query: ListingInferenceEvidenceQuery) => {
    const evidenceRow = {
        claimId: STUB_CLAIM_ID,
        listingId: query.listingId,
        kind: "partyIdentity" as ListingInferenceClaimKind,
        value: "Acme Holdings LLC",
        disposition: "proposed" as ListingInferenceDisposition,
        provenance: stubProvenance(),
        manualDecision: jsonNull(),
        nonFactStatus: "evidenceOnly",
    };
    const kindMatches = query.claimKinds.length === 0 || query.claimKinds.includes(evidenceRow.kind);
    const dispositionMatches =
        query.dispositions.length === 0 || query.dispositions.includes(evidenceRow.disposition);
    return {
    listingId: query.listingId,
    evidenceRows: kindMatches && dispositionMatches ? [evidenceRow] : [],
    gapRows: query.includeGaps
        ? [
              {
                  gap: stubGap(query.listingId),
                  relatedClaimId: jsonNull(),
                  unresolved: true,
                  rejectedOrConflicted: false,
                  provenance: stubProvenance(),
                  nonFactStatus: "containsUnresolvedGaps",
              },
          ]
        : [],
    filtersApplied: {
        claimKinds: query.claimKinds,
        dispositions: query.dispositions,
        sourceRefs: query.sourceRefs,
        unresolvedOnly: query.unresolvedOnly,
        includeGaps: query.includeGaps,
    },
    nonFactStatus: query.includeGaps ? "containsUnresolvedGaps" : "evidenceOnly",
    trace: STUB_TRACE,
};
};

const stubDecisionPayload = (request: ListingInferenceManualDecisionRequest) => {
    const acceptedDecision = {
        listingId: request.listingId,
        claimId: request.claimId,
        reviewer: request.reviewer,
        targetDisposition: request.targetDisposition,
        reason: request.reason,
        decidedAt: STUB_DECIDED_AT,
    };

    return {
        acceptedDecision,
        effectiveDispositionSummary: {
            proposed: 0,
            confirmed: request.targetDisposition === "confirmed" ? 1 : 0,
            rejected: request.targetDisposition === "rejected" ? 1 : 0,
            conflicted: request.targetDisposition === "conflicted" ? 1 : 0,
        },
        refresh: {
            kind: "included",
            workspace: stubWorkspacePayload(request.listingId),
        },
    };
};

export class StubListingInferenceReviewPortalClient implements ListingInferenceReviewPortalClient {
    private readonly failureMode: StubListingInferenceReviewPortalFailureMode;

    constructor(failureMode: OptionTs<StubListingInferenceReviewPortalFailureMode>) {
        this.failureMode = Option.fold(failureMode, () => "none", (value) => value);
    }

    readWorkspace(
        listingId: ListingInferenceListingId,
    ): TaskEitherTs<ListingInferenceReviewError, ListingInferenceWorkspaceReadResult> {
        return maybeFailure(this.failureMode, () =>
            decodeOrError(decodeListingInferenceWorkspaceReadResult(stubWorkspacePayload(listingId))),
        );
    }

    queryEvidence(
        query: ListingInferenceEvidenceQuery,
    ): TaskEitherTs<ListingInferenceReviewError, ListingInferenceEvidenceQueryResult> {
        return maybeFailure(this.failureMode, () =>
            decodeOrError(decodeListingInferenceEvidenceQueryResult(stubEvidencePayload(query))),
        );
    }

    submitDecision(
        request: ListingInferenceManualDecisionRequest,
    ): TaskEitherTs<ListingInferenceReviewError, ListingInferenceManualDecisionResult> {
        if (request.unsafeFactPromotion) {
            return taskEitherLeft(failureFromMode("unsafeFactPromotionRefused"));
        }

        return maybeFailure(this.failureMode, () =>
            decodeOrError(decodeListingInferenceManualDecisionResult(stubDecisionPayload(request))),
        );
    }
}
